import { useMemo } from 'react';
import {
  AbsoluteFill,
  Easing,
  Sequence,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

// Types
import type { Scene5bProps } from './types';

// Constants
import {
  SCENE5B_BG_EXIT_LEAD_FRAMES,
  SCENE5B_CURVED_LINE_PATH,
  SCENE5B_LINE_RETRACT_BUFFER,
  SCENE5B_SLIDE_DURATION,
} from './constants';
import { CURVED_LINES_DEFAULT_STYLE } from '@/components/EmployeeYearInReview/scenes/Scene5a/constants';
import { CLAMP_BOTH } from '@/constants';

// Utils
import { createCardGroupsWithTiming } from './utils';

// Components
import { TrainingCard } from './components/TrainingCard';

export const Scene5b = ({ trainings = [] }: Scene5bProps) => {
  const cardGroups = useMemo(
    () => createCardGroupsWithTiming(trainings),
    [trainings],
  );
  const frame = useCurrentFrame();
  const { width } = useVideoConfig();

  // Total scene duration for line-drawing animation
  const totalDuration = useMemo(() => {
    const lastGroup = cardGroups.at(-1);
    if (!lastGroup) return 0;
    return lastGroup.startFrame + lastGroup.durationInFrames;
  }, [cardGroups]);

  // Line starts retracting earlier than the last card group's slide-out
  const drawEndFrame =
    totalDuration - SCENE5B_SLIDE_DURATION - SCENE5B_BG_EXIT_LEAD_FRAMES;

  // Stroke dash offset for line-drawing effect
  const easingConfig = { ...CLAMP_BOTH, easing: Easing.inOut(Easing.ease) };
  const dashOffset =
    totalDuration > 0
      ? frame <= drawEndFrame
        ? interpolate(frame, [0, drawEndFrame], [1, 0], easingConfig)
        : interpolate(
            frame,
            [drawEndFrame, totalDuration + SCENE5B_LINE_RETRACT_BUFFER],
            [0, -1],
            easingConfig,
          )
      : 1;

  return (
    <AbsoluteFill className="overflow-hidden bg-main-gradient">
      {/* Background curved line */}
      <AbsoluteFill className="pointer-events-none">
        <svg
          className="size-full"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            d={SCENE5B_CURVED_LINE_PATH}
            pathLength={1}
            style={{
              ...CURVED_LINES_DEFAULT_STYLE,
              strokeDasharray: 1,
              strokeDashoffset: dashOffset,
            }}
          />
        </svg>
      </AbsoluteFill>

      {cardGroups.map(({ cards, startFrame, durationInFrames }, index) => {
        const isFirst = index === 0;
        const localFrame = frame - startFrame;

        // Slide-in from right
        const slideInX = isFirst
          ? 0
          : interpolate(localFrame, [0, SCENE5B_SLIDE_DURATION], [width, 0], {
              ...CLAMP_BOTH,
              easing: Easing.inOut(Easing.cubic),
            });

        // Slide-out to left (all card groups including the last one)
        const slideOutX = interpolate(
          localFrame,
          [durationInFrames - SCENE5B_SLIDE_DURATION, durationInFrames],
          [0, -width],
          {
            ...CLAMP_BOTH,
            easing: Easing.inOut(Easing.cubic),
          },
        );

        return (
          <Sequence
            key={`card-group-${index}`}
            from={startFrame}
            durationInFrames={durationInFrames}
          >
            <AbsoluteFill
              style={{ transform: `translateX(${slideInX + slideOutX}px)` }}
            >
              {cards.map((card) => (
                <TrainingCard key={card.id} card={card} frame={localFrame} />
              ))}
            </AbsoluteFill>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};
